import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { NavLink, useNavigate } from "react-router-dom";
import PayperiodLoader from "../../components/loader/PayperiodLoader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import Swal from "sweetalert2";
import toast from "../../components/sweetalert/Sweetalert";
import './payperiod.scss';

const Payperiod = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname');

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        startdate: '',
        enddate: '',
        payrolldate: '',
        status: ''
    });

    const payperiodDataKey = ['payperiods', currentPage, rowPerPage];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const tableColumns = [
        { name: 'Start Date', selector: row => row.sdate },
        { name: 'End Date', selector: row => row.edate },
        { name: 'Payroll Date', selector: row => row.pdate },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const resetForm = () => {
        setFormData({
            id: 0,
            startdate: '',
            enddate: '',
            payrolldate: '',
            status: ''
        });
    };

    const deletePayperiodMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`payperiods?uname=${uname}&id=${id}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(payperiodDataKey);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while deleting pay period!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const deletePayperiod = (id, payroll) => {
        Swal.fire({
            text: `Are you sure you want to delete payperiod details of ${payroll}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: (result) => {
				return new Promise(function(resolve) {
                    if (result) {
                        deletePayperiodMutation.mutate(id);
                        resolve();
                    }
                });
            }
        });
    };

    const { data: payperiods, isLoading: isLoadingPayperiods, error: payperiodsError } = useQuery({
        queryKey: payperiodDataKey,
        queryFn: async () => {
            const res = await axios.get(`payperiods?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const payperioddata = res.data.response?.payperiods?.map(item => ({
                sdate: item.sdate,
                edate: item.edate,
                pdate: item.pdate,
                status: item.status === 1 ? <span className="label label-success">Active</span> : <span className="label label-danger">Inactive</span>,
                action: (
                    <div>
                        <button type="button" className="btn-edit" onClick={() => setFormData({ id: item.id, startdate: item.sdateRaw, enddate: item.edateRaw, payrolldate: item.pdateRaw, status: item.status })}>
                            <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn-delete" onClick={() => deletePayperiod(item.id, item.pdate)}>
                            <i className="fa fa-trash"></i>
                        </button>
                    </div>
                )
            }));

            return { data: payperioddata, totalRows: res.data.response?.totalRows };
        },
        retry: 1,
        staleTime: Infinity
    });

    useEffect(() => {
        if (payperiodsError?.response?.data.status === 'inactive') {
            destroySession();
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
        }
    }, [payperiodsError]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    const submitPayperiodMutation = useMutation({
        mutationFn: async (payperiodData) => {
            const res = await axios.post('payperiods', payperiodData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(payperiodDataKey);
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while saving payperiod details!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitPayperiod = () => {
        const { startdate, enddate, payrolldate, status } = formData;

        if (!startdate || !enddate || !payrolldate || !status) {
            toast.fire({ icon: 'error', title: "All fields are required." });
            return;
        }

        setFormSubmit(true);
        submitPayperiodMutation.mutate({ ...formData, uname });
    };

    return (
        <div className="payperiods">
            <div className="page-header">
                <div className='title'>
                    <h1>Payperiods</h1>
                    <ul className='breadcrumbs'>
                        <li>
                            <NavLink to='/dtr/my-dtr'>DTR</NavLink>
                        </li>
                        <li>
                            <span>Payperiods</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className='left-panel'>
                    <DataTable 
                        columns={tableColumns} 
                        data={payperiods?.data || []} 
                        progressPending={isLoadingPayperiods} 
                        progressComponent={<PayperiodLoader />} 
                        pagination 
                        paginationServer 
                        paginationTotalRows={payperiods?.totalRows || 0} 
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        dense 
                        highlightOnHover 
                        responsive />
                </div>

                <div className='right-panel'>
                    <form>
                        <div className='form-group'>
                            <label>Start Date</label>
                            <input type='date' name='startdate' value={formData.startdate} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>End Date</label>
                            <input type='date' name='enddate' value={formData.enddate} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Payroll Date</label>
                            <input type='date' name='payrolldate' value={formData.payrolldate} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Status</label>
                            <select name='status' value={formData.status} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                <option value='1'>Active</option>
                                <option value='2'>Inactive</option>
                            </select>
                        </div>
                        <div className='button-group'>
                            <button type='button' onClick={handleSubmitPayperiod} disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Payperiod;
